/**
 * MaestroMappingsStepper
 *
 * Purpose: Field-by-field wizard for Maestro's Mappings flow, rendered inside the
 *   Briefing accordion. Shows all Create Media Plan fields as a horizontally
 *   scrollable chip row; each chip carries an inline status indicator and the
 *   active chip is auto-scrolled into view (ORCH-379).
 *
 *   Chips are borderless pills in three states (ORCH-426), shown by fill and
 *   icon and derived by `getChipState` from the five-way step status plus
 *   whether the brief pass is still running: **resolved** (gray fill, green
 *   check — `resolved` and `user-provided`), **needs you** (light red fill, red
 *   X — `unmatched`, `uncertain`, and `pending` once the pass has finished) and
 *   **evaluating** (gray fill, gray dot — `pending` only while the pass runs,
 *   never after). The viewed chip keeps its state's fill and is marked by bold
 *   text and a 2px teal underline beneath the pill, never a border or a teal
 *   fill, so "this is the field I'm on" and "this field needs me" are two
 *   different signals. A low-confidence match gets no amber icon of its own:
 *   it is a needs-you chip, and the suggested value and confidence are on its
 *   card. Colours are theme tokens only — the form's `error.main` (tinted) and
 *   `success.main`, the shared `maestro.surface` gray — no literals. The active field's detail panel handles pending MDM mapping
 *   approval gates inline. The chips are the only navigation (ORCH-403): any
 *   chip can be clicked at any time, so a resolved field stays reviewable and a
 *   pending gate can be reached and confirmed from the wizard.
 *
 *   There is no Back / Next row and no "n / total" counter beneath the card
 *   (ORCH-403). Both duplicated what the chips already show — position and
 *   state — and the row read as a second way to walk the stepper. This is a
 *   deliberate reversal of ORCH-277 AC6/AC7 (chevron spacing, status text in
 *   that row) and of ORCH-380's assertion that the counter stays on screen.
 *   The step's running state is carried by the workflow rail's pulsing dot in
 *   the step header, not by text inside the wizard.
 *
 *   The card itself is neutral (ORCH-403) and shaded, not outlined (ORCH-426):
 *   the shared `maestro.surface` fill and no border, whatever the active
 *   field's state — outline means "you can type here", so only the form input
 *   inside the card draws one. The chip above already carries the state icon,
 *   so the card does not repeat it with a tinted background or coloured
 *   border. If a state colour is ever reinstated here it must index
 *   `INDICATOR_BORDER` from `template-form/FieldIndicatorIcon` so the wizard
 *   and the plan form's validation borders move together.
 *
 *   Step status reads the live plan form as well as Maestro's results: a field
 *   the user already filled shows as "User-provided" rather than waiting on
 *   Maestro (ORCH-380).
 *
 *   The card is one component with four states (ORCH-412), driven by the
 *   field's evaluation result: resolved / uncertain (read-only mapped value),
 *   needs confirmation (an open mdm_mapping gate), not found (unmatched, or no
 *   result once the brief pass has finished), and user-provided (read-only).
 *   Both input states render the Create Media Plan form's own control for the
 *   field — the view's Field row from `fieldFor` through `getFieldRenderer`, via
 *   MaestroFieldControl — so labels, option lists, number formatting and
 *   multiselect shape match the form for every field type, current and future.
 *   They differ only in when a value reaches the plan, and that rule is the
 *   state's, not the control's: **a value Maestro suggested needs
 *   confirmation; a value the user supplied does not.** Needs confirmation
 *   keeps a pick in the card until Confirm (ORCH-376); not found writes each
 *   change straight to the plan form, so the two sides stay in sync both ways.
 *   "Waiting for Maestro to evaluate this field" shows only while the brief
 *   pass is running (`briefPassRunning`, owned by the parent).
 *
 *   Confirm is only enabled once there is actually something to confirm
 *   (ORCH-373) — the guard lives in `canConfirmMapping` so every field is gated
 *   the same way. Since ORCH-376 that "something" is the control's value
 *   (which shows Maestro's proposal) or a value the user typed on the plan form;
 *   Confirm itself is what writes the value into the plan. A not-found card's
 *   Save is gated by `canSubmitUserInput`, and Skip appears there only when
 *   the field is optional.
 *
 *   A settled mapping is rendered value-first (ORCH-378): the matched value is
 *   the card's bold primary text, with how it was resolved and at what
 *   confidence demoted to a muted line beneath it. The field name and status
 *   icon live on the active chip only — repeating them in the card body was the
 *   redundancy this layout replaced. A Maestro-resolved card also shows the
 *   original wording from the brief (ORCH-409): a muted "From Your Brief" row
 *   above a "Mapped To" row carrying the bold value, so the user can check a
 *   lossy mapping at a glance. Identical wording is shown once, with "Matched
 *   exactly" on the provenance line; user-provided cards have no brief row.
 *
 *   Step labels are not constants (ORCH-381): each chip shows the label the
 *   Create Media Plan form renders for that field, read from the same mediaplan
 *   edit-modal metadata view the form uses. A separate hardcoded label table is
 *   what let the wizard say "Media Agency" while the form said "Agency".
 *
 *   Skip is a single action (ORCH-372): it records the skip and advances in one
 *   click. It never approves the field's current form value — a value the user
 *   put there by hand is not their answer to this gate, and after ORCH-376 a
 *   skipped proposal leaves the field genuinely empty.
 *
 *   Skip and Confirm advance to the next *unresolved* field, not the next field
 *   in sequence (ORCH-403, `findNextUnresolvedStep`). Landing on a field that
 *   is already resolved or user-provided wastes a step; an `uncertain` field is
 *   a destination because "AI guess — please verify" is a question the user
 *   has not answered yet.
 *
 * Architecture: Controlled component — activeStep/onStepChange owned by
 *   MaestroChatPanel, which auto-advances past auto-resolved and user-provided
 *   fields. The inline approval flow (Confirm/Skip) replicates the
 *   ApprovalCard mdm_mapping branch so that mdm_mapping gates never surface
 *   in the top-level ApprovalCard (only user_input gates remain there).
 *
 * Dependencies: MaestroFieldControl (the form's FieldRenderers), createPlanFieldValues,
 *   mappingApprovalGuard (canConfirmMapping, canSubmitUserInput),
 *   OrchestratorResumeDetail, MaestroFieldResult, useMDMValues (label lookup for
 *   user-picked list values), useCreatePlanFieldLabels (step labels from the
 *   plan's metadata view — ORCH-381), MUI Box/Chip/Stack/Button/Typography/Alert,
 *   maestroSurfaceSx and the theme's `maestro.surface` / `error` / `success` tokens.
 */

import React from 'react';
import { Alert, Box, Button, Chip, Link, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import type { ApprovalRequiredEvent } from '../../types/chat';
import type { MaestroFieldResult } from './MaestroChatPanel';
import type { OrchestratorResumeDetail, ApprovalQueueItem } from './ApprovalCard';
import { resolveMdmOption, wrapForFieldShape } from './createPlanFieldValues';
import MaestroFieldControl, { isMaestroEditableField } from './MaestroFieldControl';
import { PLAN_CURRENCY_FD_KEY } from './ApprovalNumberField';
import { canConfirmMapping, canSubmitUserInput, isEmptyFieldValue } from './mappingApprovalGuard';
import { useMDMValues } from '../../services/mdmCacheService';
import { useCreatePlanFieldLabels } from '../../hooks/useModelFieldLabels';
import { maestroSurfaceSx } from './maestroSurface';
import { formatGroupedNumber } from '../../utils/formatting';
import { MIN_CONFIDENCE_THRESHOLD } from '../template-form/FieldIndicatorIcon';

/** Minimum confidence to show a green ✓ — single source of truth in FieldIndicatorIcon. */
export const MAPPINGS_MIN_CONFIDENCE = MIN_CONFIDENCE_THRESHOLD;

/** Fraction of fields that must be resolved for extraction to be considered successful. */
export const MAPPINGS_EXTRACTION_THRESHOLD = 0.5;

export interface ExtractionSummary {
  resolvedCount: number;
  totalFields: number;
  /** ok = at or above threshold; partial = some matched but below threshold; none = zero matched */
  summaryType: 'ok' | 'partial' | 'none';
}

/**
 * Summarise how well Maestro extracted fields from the brief.
 *
 * Deliberately reads Maestro's own status (not the display status from
 * getFieldStepStatus): the banner reports what Maestro pulled out of the brief,
 * so fields the user filled in by hand must not inflate the count (ORCH-380).
 */
export function computeExtractionSummary(
  fieldResults: Record<string, MaestroFieldResult>
): ExtractionSummary {
  const totalFields = CREATE_PLAN_FIELD_ORDER.length;
  const resolvedCount = CREATE_PLAN_FIELD_ORDER.reduce((count, fdKey) => {
    return getMaestroFieldStatus(fdKey, fieldResults) === 'resolved' ? count + 1 : count;
  }, 0);

  if (resolvedCount === 0) return { resolvedCount, totalFields, summaryType: 'none' };
  if (resolvedCount / totalFields < MAPPINGS_EXTRACTION_THRESHOLD) {
    return { resolvedCount, totalFields, summaryType: 'partial' };
  }
  return { resolvedCount, totalFields, summaryType: 'ok' };
}

/**
 * Create Media Plan fields the wizard walks.
 *
 * Keys only — the label shown for each step is read from the mediaplan `edit-modal`
 * metadata view at render time (ORCH-381). Hardcoding labels here is what let the chip
 * row drift from the form (chip said "Media Agency", the form's label override says
 * "Agency"). The order is the view's too (ORCH-405): the stepper sorts these keys by
 * `display_order` via `orderKeys`, so the chips follow the form. The order below is
 * only the fallback while the view loads, and mirrors the seeded view.
 */
export const CREATE_PLAN_FIELD_ORDER: ReadonlyArray<string> = [
  'fd_plan-name',
  'fd_product',
  'fd_start-date',
  'fd_end-date',
  'fd_country',
  'fd_currency',
  'fd_budget',
  'fd_target-audience',
  'fd_media-agency',
  'fd_advertiser',
  'fd_custom-plan-name',
  'fd_comments',
];

/** What Maestro's extraction pass reported for a field, ignoring form state. */
type MaestroFieldStatus = 'resolved' | 'uncertain' | 'unmatched' | 'pending';

/** Display status for a wizard step — Maestro's view plus the user-provided case. */
export type FieldStepStatus = MaestroFieldStatus | 'user-provided';

function getMaestroFieldStatus(
  fdKey: string,
  fieldResults: Record<string, MaestroFieldResult>
): MaestroFieldStatus {
  const result = fieldResults[fdKey];
  if (!result || result.status === 'pending') return 'pending';
  if (result.status === 'matched') {
    return result.confidence >= MAPPINGS_MIN_CONFIDENCE ? 'resolved' : 'uncertain';
  }
  return 'unmatched';
}

/**
 * Status shown for one wizard step.
 *
 * ORCH-380: a field the user has already filled on the plan form must never
 * read as "waiting for Maestro" (pending) or as unresolvable (unmatched) — the
 * user's own value answers the question either way, so it wins. Maestro's
 * `matched` verdicts are left alone so a low-confidence guess keeps its
 * "please verify" prompt, matching getFieldIndicatorState on the form side.
 */
export function getFieldStepStatus(
  fdKey: string,
  fieldResults: Record<string, MaestroFieldResult>,
  hasFormValue: boolean
): FieldStepStatus {
  const status = getMaestroFieldStatus(fdKey, fieldResults);
  if (hasFormValue && (status === 'pending' || status === 'unmatched')) return 'user-provided';
  return status;
}

/**
 * What a chip looks like (ORCH-426). Three states, not five: the chip tells the
 * user whether a field is done, needs them, or is still being evaluated.
 */
export type ChipState = 'resolved' | 'needs-you' | 'evaluating';

/**
 * Collapse a step status into the chip's visual state.
 *
 * Logic: `resolved` and `user-provided` are both settled. `pending` means two
 *   different things, and the split is the whole point: while the brief pass is
 *   still running the field is being evaluated; once it has finished, no result
 *   means Maestro did not find it, so the chip must ask for the user (ORCH-412
 *   — "Evaluating" never stays after evaluation ends). `uncertain` is a
 *   question the user has not answered, so it needs them too; its suggested
 *   value and confidence are shown on the card, not as a chip icon.
 * Input: status (from getFieldStepStatus), briefPassRunning (owned by the panel)
 * Output: the chip state
 */
export function getChipState(status: FieldStepStatus, briefPassRunning: boolean): ChipState {
  if (status === 'resolved' || status === 'user-provided') return 'resolved';
  if (status === 'pending' && briefPassRunning) return 'evaluating';
  return 'needs-you';
}

/** Icon inside the chip: green check, red X, or a gray dot while evaluating. */
function ChipStateIcon({ state }: { state: ChipState }) {
  switch (state) {
    case 'resolved':
      return (
        <CheckIcon
          data-testid="chip-icon-resolved"
          sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }}
        />
      );
    case 'needs-you':
      return (
        <CloseIcon
          data-testid="chip-icon-needs-you"
          sx={{ fontSize: 14, color: 'error.main', flexShrink: 0 }}
        />
      );
    case 'evaluating':
      return (
        <Box
          component="span"
          data-testid="chip-icon-evaluating"
          sx={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'action.disabled',
            flexShrink: 0,
          }}
        />
      );
  }
}

/** Opacity of the error tint behind a needs-you chip, and the darker hover step. */
const NEEDS_YOU_TINT = 0.12;
const NEEDS_YOU_TINT_HOVER = 0.2;

/**
 * True when a Create Media Plan form value counts as supplied by the user.
 * Exported so MaestroChatPanel's auto-advance and the stepper agree on what
 * "already filled" means (ORCH-380).
 */
export const hasUserValue = (v: unknown): boolean => !isEmptyFieldValue(v);

/**
 * True when a wizard step still needs the user's attention.
 *
 * ORCH-403: only `resolved` and `user-provided` are settled. `uncertain` is a
 * low-confidence guess the user has not verified, `unmatched` is a field with
 * no value, and `pending` is one Maestro has not reported on — all three are
 * places the wizard should land, never skip past.
 */
export const isUnresolvedStepStatus = (status: FieldStepStatus): boolean =>
  status !== 'resolved' && status !== 'user-provided';

/**
 * Index of the next field that still needs the user, after acting on `fromStep`.
 *
 * Logic: scans forward from `fromStep + 1` to the end of `order`,
 *   then wraps and scans from 0 up to (not including) `fromStep`. The field just
 *   acted on is never a candidate — Skip is the user declining it, and bouncing
 *   straight back would be a loop. Returns `null` when every other field is
 *   settled, which is the end-of-step case: the wizard stays where it is and the
 *   orchestrator's own completion carries the panel on to the next workflow step.
 * Input: fromStep (index acted on), fieldResults, getFormFieldValue (live plan form reader),
 *        order (the wizard's field keys in view order; defaults to the fallback order)
 * Output: step index to land on, or null when nothing unresolved remains
 */
export function findNextUnresolvedStep(
  fromStep: number,
  fieldResults: Record<string, MaestroFieldResult>,
  getFormFieldValue: (fdKey: string) => unknown,
  order: ReadonlyArray<string> = CREATE_PLAN_FIELD_ORDER
): number | null {
  const total = order.length;
  for (let offset = 1; offset < total; offset += 1) {
    const idx = (fromStep + offset) % total;
    const fdKey = order[idx];
    const status = getFieldStepStatus(fdKey, fieldResults, hasUserValue(getFormFieldValue(fdKey)));
    if (isUnresolvedStepStatus(status)) return idx;
  }
  return null;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Stable empty filter object for useMDMValues (avoids a new identity each render). */
const NO_MDM_FILTERS = {};

/** Longest raw value rendered inline before it is ellipsised (comments can be long). */
const MAX_VALUE_DISPLAY_CHARS = 120;

/** Fraction digits kept when a numeric form value is shown — matches the renderer's storage precision. */
const MAX_LITERAL_DECIMALS = 5;

/** Form values are stored as GLOBAL MDM UUIDs for list fields; everything else is literal. */
const needsMdmLookup = (scalar: unknown): boolean =>
  typeof scalar === 'string' && UUID_PATTERN.test(scalar.trim());

/** Numbers (e.g. a budget the user typed) are grouped the same way the plan form shows them (ORCH-374). */
const formatRawValue = (scalar: unknown): string => {
  const s =
    typeof scalar === 'number'
      ? formatGroupedNumber(scalar, MAX_LITERAL_DECIMALS)
      : typeof scalar === 'string'
        ? scalar.trim()
        : String(scalar);
  return s.length > MAX_VALUE_DISPLAY_CHARS ? `${s.slice(0, MAX_VALUE_DISPLAY_CHARS)}…` : s;
};

/**
 * MappingValue — the bold headline line of a settled mapping card
 *
 * Purpose: ORCH-378 — the value is what the reviewer came to read, so it leads
 *          the card as primary text rather than the resolution method.
 * Input: children (already-formatted display text), testId
 * Output: Bold body2 line that wraps instead of overflowing the panel
 */
const MappingValue: React.FC<{ children: React.ReactNode; testId: string }> = ({
  children,
  testId,
}) => (
  <Typography
    variant="body2"
    color="text.primary"
    sx={{ fontWeight: 600, wordBreak: 'break-word' }}
    data-testid={testId}
  >
    {children}
  </Typography>
);

/** Join the muted metadata parts of a mapping card with a middot, dropping empties. */
const joinProvenance = (...parts: Array<string | null | undefined | false>): string =>
  parts.filter(Boolean).join(' · ');

/** Muted row label of a mapping card ("From Your Brief", "Mapped To"). */
const MappingRowLabel: React.FC<{ children: React.ReactNode; testId: string }> = ({
  children,
  testId,
}) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ whiteSpace: 'nowrap', lineHeight: 1.6 }}
    data-testid={testId}
  >
    {children}
  </Typography>
);

/**
 * BriefSourceValue — the original brief wording on a resolved mapping card
 *
 * Purpose: ORCH-409 — show what Maestro actually read in the brief so the user
 *          can check the mapping without opening the file.
 * Logic: Regular weight and muted, so the mapped value beneath it keeps the
 *        emphasis. Text past MAX_VALUE_DISPLAY_CHARS is ellipsised by
 *        formatRawValue; a text toggle (no icon — the card body carries none)
 *        expands it to the full wording.
 * Input: value (raw source_term from the field result)
 * Output: The source text, with a Show More / Show Less toggle when truncated
 */
const BriefSourceValue: React.FC<{ value: string }> = ({ value }) => {
  const [expanded, setExpanded] = React.useState(false);
  const full = value.trim();
  const truncated = full.length > MAX_VALUE_DISPLAY_CHARS;

  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ wordBreak: 'break-word', lineHeight: 1.6 }}
      data-testid="mapping-source"
    >
      {expanded ? full : formatRawValue(full)}
      {truncated && (
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="hover"
          sx={{ ml: 0.5, verticalAlign: 'baseline' }}
          onClick={() => setExpanded((prev) => !prev)}
          data-testid="mapping-source-toggle"
        >
          {expanded ? 'Show Less' : 'Show More'}
        </Link>
      )}
    </Typography>
  );
};

/**
 * MappingSummary — value-first layout for a settled field mapping
 *
 * Purpose: ORCH-378 — lead with the matched value and demote how it was resolved
 *          (and at what confidence) to a muted line underneath. ORCH-409 adds the
 *          original brief wording as a labelled row above the value.
 * Logic: Neither the field name nor a status icon belongs in the card body — the
 *        active chip above it already carries both, and repeating them is the
 *        redundancy this layout replaces. When a sourceValue is given the card
 *        reads as a flow: "From Your Brief" (muted) then "Mapped To" (the bold
 *        value that gets saved), in a two-column grid so the values line up,
 *        then a gap before the provenance line. Without one (user-provided, or
 *        the brief wording matched the value exactly) the value stands alone.
 * Input: value (headline element; omitted when there is nothing to show, e.g. an
 *        MDM label still loading), sourceValue (optional raw brief wording),
 *        provenance (muted line), optional testId
 * Output: The optional source row and the value line, then the provenance line
 */
const MappingSummary: React.FC<{
  value?: React.ReactNode;
  sourceValue?: string;
  provenance: string;
  testId?: string;
}> = ({ value, sourceValue, provenance, testId }) => (
  <Stack spacing={sourceValue ? 0.75 : 0.25} data-testid={testId}>
    {sourceValue ? (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          columnGap: 1,
          rowGap: 0.25,
          alignItems: 'baseline',
        }}
        data-testid="mapping-source-rows"
      >
        <MappingRowLabel testId="mapping-source-label">From Your Brief</MappingRowLabel>
        <BriefSourceValue value={sourceValue} />
        {value && (
          <>
            <MappingRowLabel testId="mapping-value-label">Mapped To</MappingRowLabel>
            {value}
          </>
        )}
      </Box>
    ) : (
      value
    )}
    <Typography variant="caption" color="text.secondary" data-testid="mapping-provenance">
      {provenance}
    </Typography>
  </Stack>
);

/**
 * UserProvidedValue — renders the value the user put on the plan form
 *
 * Purpose: Show the user's own selection in the wizard panel instead of a
 *          "waiting for Maestro" placeholder (ORCH-380).
 * Logic: Multiselect list fields hold arrays — take the first element. When the
 *        stored scalar is an MDM UUID, resolve it to its option name through the
 *        shared MDM cache (same matching as the approval dropdown); the lookup
 *        is skipped entirely for literal values (dates, budget, free text) so
 *        non-list fields fire no request. While the lookup is in flight, or if
 *        the id resolves to nothing, render nothing — the active chip already
 *        names the field, so a bare UUID would be worse than no value line.
 * Input: fdKey, tenantId, value (raw form value)
 * Output: Headline line with the resolved label, or null when nothing to show
 */
const UserProvidedValue: React.FC<{ fdKey: string; tenantId: string; value: unknown }> = ({
  fdKey,
  tenantId,
  value,
}) => {
  const scalar = Array.isArray(value) ? value[0] : value;
  const lookup = needsMdmLookup(scalar);
  const { data: options } = useMDMValues(fdKey, tenantId, NO_MDM_FILTERS, { enabled: lookup });
  const resolved = lookup ? resolveMdmOption(scalar, options ?? []) : null;
  const display = lookup ? (resolved?.name ?? '') : formatRawValue(scalar);

  if (!display) return null;
  return <MappingValue testId="mapping-user-provided-value">{display}</MappingValue>;
};

export interface MaestroMappingsStepperProps {
  /** Per-field match results accumulated from Maestro (ORCH-215). */
  fieldResults: Record<string, MaestroFieldResult>;
  /** Active mdm_mapping approval gates from the orchestrator. */
  mappingApprovalItems: ApprovalQueueItem[];
  /** Currently focused field index (controlled by parent). */
  activeStep: number;
  /** Called when user navigates to a different step. */
  onStepChange: (index: number) => void;
  /** Read the current plan form value for a given field-def key. */
  getFormFieldValue: (fdKey: string) => unknown;
  tenantId: string;
  /** Apply field values to the plan form (TemplateForm applyAssistantDraft). */
  onApplyCreatePlanFields?: (fields: Record<string, unknown>) => void;
  /** Resume the orchestrator after an approval decision. */
  onOrchestratorResume?: (detail: OrchestratorResumeDetail) => void | Promise<void>;
  /** Remove an approval item from the queue after it is resolved. */
  onApprovalResolved: (key: string) => void;
  /** Surface an error to the parent / user. */
  onError: (message: string) => void;
  /** Disable approval inputs while the orchestrator stream is active. */
  disabled?: boolean;
  /**
   * True while Maestro's brief pass is still running (ORCH-412 §3.5). A field
   * with no result reads "Waiting for Maestro…" only while this holds; once the
   * pass has finished, no result means not found and the card offers the input.
   * Owned by MaestroChatPanel — the stepper never computes lifecycle state.
   */
  briefPassRunning: boolean;
  runId: string | null;
  sessionId: string | null;
}

const MaestroMappingsStepper: React.FC<MaestroMappingsStepperProps> = ({
  fieldResults,
  mappingApprovalItems,
  activeStep,
  onStepChange,
  getFormFieldValue,
  tenantId,
  onApplyCreatePlanFields,
  onOrchestratorResume,
  onApprovalResolved,
  onError,
  disabled,
  briefPassRunning,
  runId,
  sessionId,
}) => {
  const [busyKey, setBusyKey] = React.useState<string | null>(null);
  /**
   * What the user picked in a gate's control, by approval key — local to the
   * card until Confirm (ORCH-376). Absent means untouched: the control shows the
   * plan form's value, else Maestro's proposal.
   */
  const [gateDraft, setGateDraft] = React.useState<Record<string, unknown>>({});
  const [mappingHint, setMappingHint] = React.useState<Record<string, string>>({});
  /**
   * The field the user is typing into from a not-found card (ORCH-412). The
   * first keystroke makes the field user-provided; without this the card would
   * swap to the read-only summary mid-entry. Cleared on Save / Skip and when
   * the active step changes.
   */
  const [editingFdKey, setEditingFdKey] = React.useState<string | null>(null);

  /**
   * Labels mirror the Create Media Plan form exactly — same metadata view
   * (ORCH-381) — and so do the controls: `fieldFor` hands back the view's own
   * Field row for the form's renderer (ORCH-374 / ORCH-412).
   */
  const { labelFor, fieldFor, orderKeys } = useCreatePlanFieldLabels(tenantId);

  /** Chip order is the form's order — the metadata view's display_order (ORCH-405). */
  const fieldOrder = React.useMemo(() => orderKeys(CREATE_PLAN_FIELD_ORDER), [orderKeys]);

  const totalSteps = fieldOrder.length;
  const safeStep = Math.max(0, Math.min(activeStep, totalSteps - 1));
  const activeFdKey: string | undefined = fieldOrder[safeStep];

  /** Chip DOM nodes by field-def key, so the active one can be scrolled into view. */
  const chipRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  /**
   * ORCH-379: keep the active chip on screen. The chip row scrolls horizontally
   * and twelve chips never fit, so Next/Back (and the parent's auto-advance past
   * high-confidence fields) used to leave the active field off-screen with no
   * visible selection. `block: 'nearest'` keeps the surrounding chat panel from
   * jumping vertically; `inline: 'center'` centres the chip within the row.
   * Optional call because jsdom does not implement scrollIntoView.
   */
  React.useEffect(() => {
    const activeKey = fieldOrder[safeStep];
    if (!activeKey) return;
    chipRefs.current[activeKey]?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [fieldOrder, safeStep]);

  React.useEffect(() => {
    setEditingFdKey(null);
  }, [safeStep]);

  /** The pending approval gate for the currently active field (at most one). */
  const activePendingItem = activeFdKey
    ? (mappingApprovalItems.find((item) => item.data.mapping?.fd_key === activeFdKey) ?? null)
    : null;

  /** Options for the open gate's field — names the user's pick in the resume payload. */
  const activeGateFdKey = activePendingItem?.data.mapping?.fd_key;
  const { data: gateOptions } = useMDMValues(activeGateFdKey ?? '', tenantId, NO_MDM_FILTERS, {
    enabled: !!activeGateFdKey,
  });

  /**
   * The value a gate's control shows, and what Confirm sends: the user's pick in
   * the card, else the plan form's value, else Maestro's proposal (display only
   * until Confirm — ORCH-376), in the shape the form declares for the field.
   */
  const gateValue = (item: ApprovalQueueItem): unknown => {
    const mapping = item.data.mapping;
    if (!mapping) return undefined;
    if (item.key in gateDraft) return gateDraft[item.key];
    const formValue = getFormFieldValue(mapping.fd_key);
    if (hasUserValue(formValue)) return formValue;
    return isEmptyFieldValue(mapping.proposed_value)
      ? undefined
      : wrapForFieldShape(mapping.fd_key, mapping.proposed_value);
  };

  /**
   * Move on after Confirm or Skip — to the next field that still needs the
   * user (ORCH-403), or nowhere when none remains: the field just acted on then
   * stays on screen with its settled card while the run carries on.
   */
  const advanceToNextUnresolved = () => {
    const next = findNextUnresolvedStep(safeStep, fieldResults, getFormFieldValue, fieldOrder);
    if (next !== null) onStepChange(next);
  };

  /**
   * Confirm the active field's mapping.
   *
   * ORCH-376: Confirm is the write. Nothing seeds the plan form any more, so the
   * value only reaches the plan here — the same `choice_value` that goes to the
   * orchestrator is applied to the form, which keeps the two in step whether the
   * user picked an option or accepted Maestro's proposal as shown.
   */
  const handleApprove = async (item: ApprovalQueueItem) => {
    const ev = item.data as ApprovalRequiredEvent;
    if (!ev.mapping) return;

    const fd = ev.mapping.fd_key;
    // Mirrors canConfirmMapping: exactly what the card's control shows.
    const choice_value = gateValue(item);

    if (isEmptyFieldValue(choice_value)) {
      setMappingHint((h) => ({ ...h, [item.key]: 'Choose a value before approving.' }));
      return;
    }

    const choice_label =
      resolveMdmOption(choice_value, gateOptions ?? [])?.name ||
      (typeof ev.mapping.proposed_label === 'string' ? ev.mapping.proposed_label : '') ||
      String(Array.isArray(choice_value) ? choice_value[0] : choice_value);

    // Clear hint
    setMappingHint((h) => {
      const n = { ...h };
      delete n[item.key];
      return n;
    });

    if (!runId || !sessionId) {
      onError('Missing orchestrator thread — cannot resume. Try sending another message.');
      return;
    }

    setBusyKey(item.key);
    try {
      const detail: OrchestratorResumeDetail = {
        decision: 'approved',
        mapping: {
          fd_key: fd,
          field_label: ev.mapping.field_label,
          choice_label,
          choice_value: Array.isArray(choice_value) ? choice_value[0] : choice_value,
        },
      };
      // The explicit user action that puts the value in the plan (ORCH-376).
      onApplyCreatePlanFields?.({ [fd]: wrapForFieldShape(fd, choice_value) });
      onApprovalResolved(item.key);
      // Advance to the next unresolved field after confirming (ORCH-403).
      advanceToNextUnresolved();
      await onOrchestratorResume?.(detail);
    } catch (err) {
      onError(err instanceof Error ? `Could not resume: ${err.message}` : 'Could not resume.');
    } finally {
      setBusyKey(null);
    }
  };

  /**
   * Skip the active field — one action, no intermediate Continue step (ORCH-372).
   *
   * Always resumes with `rejected` and never sends the field's current value:
   * Skip is the user declining to answer, so whatever happens to be on the form
   * is not their answer to this gate. Since ORCH-376 nothing seeds the form, so
   * there is also nothing to clear — the field is simply still empty, which is
   * what makes the orchestrator's "select the value in the form field" feedback
   * honest. Advancing mirrors handleApprove so Skip and Confirm both land on
   * the next unresolved field (ORCH-403).
   */
  const handleSkip = async (item: ApprovalQueueItem) => {
    if (!runId || !sessionId) {
      onError('Missing orchestrator thread — cannot resume. Try sending another message.');
      return;
    }

    setBusyKey(item.key);
    try {
      onApprovalResolved(item.key);
      advanceToNextUnresolved();
      await onOrchestratorResume?.({ decision: 'rejected' });
    } catch (err) {
      onError(err instanceof Error ? `Could not resume: ${err.message}` : 'Could not resume.');
    } finally {
      setBusyKey(null);
    }
  };

  const renderChipRow = () => (
    <Box
      sx={{
        overflowX: 'auto',
        display: 'flex',
        gap: 0.75,
        // Room beneath the pills for the viewed chip's underline, which sits
        // outside the pill; the row is a scroll box, so it would clip otherwise.
        pb: '8px',
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'action.hover', borderRadius: 2 },
      }}
      role="tablist"
      aria-label="Field mapping steps"
    >
      {fieldOrder.map((fdKey, idx) => {
        const status = getFieldStepStatus(
          fdKey,
          fieldResults,
          hasUserValue(getFormFieldValue(fdKey))
        );
        const state = getChipState(status, briefPassRunning);
        const label = labelFor(fdKey);
        const isActive = idx === safeStep;

        return (
          <Chip
            key={fdKey}
            ref={(el: HTMLDivElement | null) => {
              chipRefs.current[fdKey] = el;
            }}
            data-testid={`mapping-step-${fdKey}`}
            data-status={status}
            data-state={state}
            role="tab"
            aria-selected={isActive}
            aria-label={`${label} — ${status}`}
            label={
              <Stack direction="row" alignItems="center" gap={0.5} sx={{ px: 0.25 }}>
                <ChipStateIcon state={state} />
                <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
              </Stack>
            }
            size="small"
            onClick={() => onStepChange(idx)}
            // Fill and icon say the state; bold text and the underline say
            // "viewed". No border in any state (ORCH-426) — the coloured chip
            // borders clashed with the form's red / green validation borders.
            sx={(theme) => ({
              flexShrink: 0,
              cursor: 'pointer',
              position: 'relative',
              overflow: 'visible',
              border: 'none',
              bgcolor:
                state === 'needs-you'
                  ? alpha(theme.palette.error.main, NEEDS_YOU_TINT)
                  : 'maestro.surface',
              color: 'text.primary',
              fontWeight: isActive ? 700 : 500,
              '& .MuiChip-label': { display: 'flex', alignItems: 'center', px: 0.75 },
              // Hover and keyboard focus darken the state's own fill a step; MUI's
              // clickable-chip default would swap in a neutral gray and lose the state.
              '&:hover, &.Mui-focusVisible': {
                bgcolor:
                  state === 'needs-you'
                    ? alpha(theme.palette.error.main, NEEDS_YOU_TINT_HOVER)
                    : theme.palette.grey[200],
              },
              ...(isActive
                ? {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: 10,
                      right: 10,
                      bottom: -5,
                      height: 2,
                      borderRadius: 1,
                      bgcolor: 'primary.main',
                    },
                  }
                : {}),
            })}
          />
        );
      })}
    </Box>
  );

  const renderActiveFieldPanel = () => {
    if (!activeFdKey) return null;

    const fdKey = activeFdKey;
    const formValue = getFormFieldValue(fdKey);
    const status = getFieldStepStatus(fdKey, fieldResults, hasUserValue(formValue));
    const result = fieldResults[fdKey];

    const field = fieldFor(fdKey);
    const currencyFieldValue = getFormFieldValue(PLAN_CURRENCY_FD_KEY);

    // --- Needs confirmation: an open mapping gate ---
    if (activePendingItem) {
      const item = activePendingItem;
      const ev = item.data as ApprovalRequiredEvent;
      const shown = gateValue(item);

      // ORCH-373: Confirm stays disabled until the field holds a value, so an
      // empty field can't be confirmed by accident. Skip remains available —
      // it is how the user deliberately moves past a field they can't answer.
      const canConfirm = canConfirmMapping(
        formValue,
        shown === undefined ? null : { label: '', value: shown }
      );

      return (
        <Box data-testid="mapping-needs-confirmation">
          {ev.mapping?.explanation ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {ev.mapping.explanation}
            </Typography>
          ) : ev.detail ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {ev.detail}
            </Typography>
          ) : null}
          {ev.mapping?.brief_term ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Brief reference: &quot;{ev.mapping.brief_term}&quot;
            </Typography>
          ) : null}
          {isMaestroEditableField(field) ? (
            <Box sx={{ mt: 0.5 }}>
              <MaestroFieldControl
                field={field}
                tenantId={tenantId}
                value={shown}
                // ORCH-376 — a value Maestro suggested needs confirmation. The
                // control shows the proposal, and a pick here stays in the card;
                // only Confirm (handleApprove) writes to the plan form. Writing on
                // change here is what made a low-confidence guess look resolved
                // and left skipped proposals in saved plans (ORCH-376, ORCH-391,
                // ORCH-392). Contrast the not-found state below, which does write.
                onChange={(next) => setGateDraft((prev) => ({ ...prev, [item.key]: next }))}
                disabled={!!busyKey || disabled}
                currencyFieldValue={currencyFieldValue}
              />
            </Box>
          ) : ev.mapping?.proposed_label ? (
            <MappingValue testId="mapping-proposed-value">{ev.mapping.proposed_label}</MappingValue>
          ) : null}
          {mappingHint[item.key] ? (
            <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
              {mappingHint[item.key]}
            </Alert>
          ) : null}
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} justifyContent="flex-end">
            <Button
              size="small"
              color="error"
              variant="outlined"
              data-testid="mapping-skip-btn"
              disabled={!!busyKey || disabled}
              onClick={() => handleSkip(item)}
            >
              Skip
            </Button>
            <Button
              size="small"
              variant="contained"
              data-testid="mapping-confirm-btn"
              disabled={!!busyKey || disabled || !canConfirm}
              onClick={() => handleApprove(item)}
            >
              {busyKey === item.key ? '…' : 'Confirm'}
            </Button>
          </Stack>
        </Box>
      );
    }

    // --- Not found: Maestro has no value, so the user gets the form's own input ---
    // Unmatched, or pending once the brief pass has finished (a free-text field
    // such as Comments is never evaluated), or a field the user is mid-entry on.
    const notFound =
      editingFdKey === fdKey ||
      status === 'unmatched' ||
      (status === 'pending' && !briefPassRunning);
    if (notFound && isMaestroEditableField(field)) {
      const optional = !field.is_required;
      const handleLeave = () => {
        setEditingFdKey(null);
        advanceToNextUnresolved();
      };
      return (
        <Box data-testid="mapping-not-found">
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1 }}
            data-testid="mapping-not-found-hint"
          >
            Not found in the brief. Enter it here.
          </Typography>
          <MaestroFieldControl
            field={field}
            tenantId={tenantId}
            value={formValue}
            // ORCH-412 §3.3 — a value the user supplied does not need
            // confirmation: there is no proposal, so the entry is the answer and
            // writes straight through to the plan form, keeping both sides in
            // sync. Never copy this into the needs-confirmation state (ORCH-376).
            onChange={(next) => {
              setEditingFdKey(fdKey);
              onApplyCreatePlanFields?.({ [fdKey]: next });
            }}
            currencyFieldValue={currencyFieldValue}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} justifyContent="flex-end">
            {optional && (
              <Button
                size="small"
                color="error"
                variant="outlined"
                data-testid="mapping-skip-btn"
                onClick={handleLeave}
              >
                Skip
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              data-testid="mapping-save-btn"
              disabled={!canSubmitUserInput(formValue)}
              onClick={handleLeave}
            >
              Save
            </Button>
          </Stack>
        </Box>
      );
    }

    // "Waiting" is only true while the brief pass is still running (ORCH-412).
    if (status === 'pending' && briefPassRunning) {
      return (
        <Typography variant="caption" color="text.secondary" data-testid="mapping-waiting">
          Waiting for Maestro to evaluate this field…
        </Typography>
      );
    }

    // ORCH-380: the user got here first — show their value, don't re-evaluate it.
    if (status === 'user-provided') {
      return (
        <MappingSummary
          testId="mapping-user-provided"
          value={<UserProvidedValue fdKey={fdKey} tenantId={tenantId} value={formValue} />}
          provenance="User-provided · taken from your entry on the plan form"
        />
      );
    }

    if (status === 'resolved' || status === 'uncertain') {
      // The resolved label is the mapped value; source_term is the brief wording
      // it came from, and is all there is to show for free-text fields.
      const display = result?.resolved_label || result?.source_term;
      // ORCH-409: the brief wording gets its own "From Your Brief" row above the
      // value when it differs from it (a list field maps "US" → "United States").
      // When it is the same text the value is shown once and the provenance line
      // says so instead of repeating it.
      const sourceTerm = result?.source_term?.trim();
      const matchedExactly = !!sourceTerm && sourceTerm === display?.trim();

      return (
        <MappingSummary
          testId={`mapping-${status}`}
          value={
            display ? (
              <MappingValue testId="mapping-value">{formatRawValue(display)}</MappingValue>
            ) : undefined
          }
          sourceValue={sourceTerm && !matchedExactly ? sourceTerm : undefined}
          provenance={joinProvenance(
            status === 'resolved' ? 'Auto-resolved by Maestro' : 'AI guess — please verify',
            `${Math.round((result?.confidence ?? 0) * 100)}% confidence`,
            matchedExactly && 'Matched exactly'
          )}
        />
      );
    }

    // Not found, but no control to offer: the view has not loaded yet, or the
    // field is a file upload, which a wizard card does not host (ORCH-412 R2).
    return (
      <Typography variant="body2" color="text.secondary" data-testid="mapping-unmatched">
        Maestro could not resolve this field
      </Typography>
    );
  };

  return (
    <Box data-testid="maestro-mappings-stepper">
      {renderChipRow()}

      {/*
        Active field detail panel. Neutral on purpose (ORCH-403): the chip above
        already carries the field's state, so the card does not repeat it with a
        tinted background or a coloured border. Shaded, not outlined (ORCH-426):
        the card is Maestro's, so it takes the shared gray surface; only the
        form input inside it draws an outline. Skip / Confirm live inside this
        card, bottom-right, and are its only actions — there is no Back / Next
        row and no counter beneath it (see the file header for the reversal).
      */}
      <Box
        data-testid="mapping-active-panel"
        sx={{
          ...maestroSurfaceSx,
          mt: 1,
          p: 1.25,
          minHeight: 56,
        }}
      >
        {renderActiveFieldPanel()}
      </Box>
    </Box>
  );
};

export default MaestroMappingsStepper;
